import React, { useEffect, useState } from 'react';
import { ActionItem, ContentBlock } from '../../../../types';
import Textarea from '@ramsey-design-system/textarea';
import Button from '@ramsey-design-system/button';
import ToastMessage from './ToastMessage';

// Styles
import '@ramsey-design-system/textarea/dist/textarea.css';
import '@ramsey-design-system/button/dist/button.css';
import styles from './response-question-template.module.css';
import { saveQuestionProgress } from '../../../../lib/learnClient';

type ResponseQuestionTemplateProps = {
  actionItem: ActionItem;
  onUpdate: any;
};

function renderContentBlocks(contentBlocks?: ContentBlock[] | null) {
  if (!contentBlocks) {
    return null;
  }

  return (
    <>
      {contentBlocks?.map(block => {
        if (block.content_type === 'paragraph-block') {
          return (
            <p key={block.text} className={styles.paragraph}>
              {block.text}
            </p>
          );
        }

        return null;
      })}
    </>
  );
}

function ResponseQuestionTemplate({
  actionItem,
  onUpdate,
}: ResponseQuestionTemplateProps) {
  const {
    question_response: response,
    body: { content_blocks: contentBlocks } = {},
    course_id: courseId,
    lesson_id: lessonId,
    id,
  } = actionItem;

  const [text, setText] = useState(response);
  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    setText(actionItem.question_response);
  }, [actionItem]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setText(event.target.value);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = new URLSearchParams({
      'action_item[question_response]': text as string,
    }).toString();

    setIsSaving(true);

    saveQuestionProgress({
      course_id: courseId,
      lesson_id: lessonId,
      action_id: id,
      data: payload,
      template: actionItem.template,
    })
      .then(() => {
        setShowToast('success');
        onUpdate({ ...actionItem, question_response: text });
      })
      .catch(() => setShowToast('error'))
      .finally(() => setIsSaving(false));
  };

  const isButtonDisabled = !Boolean(text) || text === response || isSaving;

  return (
    <div>
      {renderContentBlocks(contentBlocks)}

      <form onSubmit={handleSubmit} className={styles.form}>
        <Textarea
          autoFocus
          rows="5"
          size="lg"
          placeholder="Go ahead. Answer it here."
          value={text || ''}
          onChange={handleChange}
          className={styles.textarea}
        />

        <div className={styles.buttonWrapper}>
          <Button
            type="submit"
            size="large"
            fullWidth
            disabled={isButtonDisabled}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <ToastMessage setToast={setShowToast} type={showToast} />
        </div>
      </form>
    </div>
  );
}

export default ResponseQuestionTemplate;
